import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { formatEther } from 'viem';
import { useBalance, useReadContracts } from 'wagmi';
import {
  RiArrowDownSLine,
  RiCalendarLine,
  RiCoinsLine,
  RiExternalLinkLine,
  RiFundsLine,
  RiLoader4Line,
  RiPieChartLine,
  RiPlantLine,
  RiStackLine,
  RiErrorWarningLine,
} from '@remixicon/react';
import { AppSidebar } from '@/components/app-sidebar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/breadcrumb';
import { Separator } from '@/components/separator';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/card';
import { Badge } from '@/components/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/collapsible';
import { LARISSA_DAO_CONTRACT_CONFIG } from '@/configs/larissa-dao.config';
import { larissaChain } from '@/configs/chain';
import { useStats } from '@/providers/global-data.provider';
import { cn } from '@/utils/utils';

/** Matches `LarissaDaoV4.uintToString` — month is not zero-padded (`2024-6`). */
export function buildDaoMonthKeys(startYear: number, startMonth: number, end: Date): string[] {
  const endYear = end.getFullYear();
  const endMonth = end.getMonth() + 1;
  const keys: string[] = [];
  let y = startYear;
  let m = startMonth;
  while (y < endYear || (y === endYear && m <= endMonth)) {
    keys.push(`${y}-${m}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return keys;
}

export function formatDaoMonthLabel(monthKey: string): string {
  const [ys, ms] = monthKey.split('-');
  const year = Number(ys);
  const month = Number(ms);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return monthKey;
  return new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

/** Native LRS–only reads for this dashboard (see LarissaDaoV4). */
const MONTHLY_FNS = [
  'membersRewardTotal',
  'nftRewardFinalize',
  'nftLastID',
  'perL1NftReward',
  'perL2NftReward',
  'perL3NftReward',
] as const;

const GLOBAL_FNS = ['projectFunding', 'developmentFarmAsic', 'exchangeListingReward'] as const;

type MonthlyRow = {
  key: string;
  label: string;
  membersLrs: bigint;
  finalized: boolean;
  nftLastID: bigint;
  perL1: bigint;
  perL2: bigint;
  perL3: bigint;
};

function fmtLrs(wei: bigint, usdPrice: number): { lrs: string; usd: string | null } {
  const n = Number(formatEther(wei));
  const lrs = n.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  if (!usdPrice || !Number.isFinite(usdPrice)) return { lrs, usd: null };
  const usd = (n * usdPrice).toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
  return { lrs, usd };
}

function StatTile({
  title,
  subtitle,
  lrsLine,
  usdLine,
  icon: Icon,
  className,
}: {
  title: string;
  subtitle?: string;
  lrsLine: string;
  usdLine: string | null;
  icon: typeof RiFundsLine;
  className?: string;
}) {
  return (
    <Card
      className={cn(
        'border-border bg-sidebar/80 backdrop-blur-sm transition-colors duration-200 hover:border-primary/25 cursor-default',
        className,
      )}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
              <Icon className="size-5 text-amber-500 shrink-0" aria-hidden />
              {title}
            </CardTitle>
            {subtitle ? <CardDescription className="text-xs mt-1">{subtitle}</CardDescription> : null}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-lg font-semibold tabular-nums text-foreground">{lrsLine}</p>
        {usdLine ? <p className="text-sm text-muted-foreground tabular-nums mt-1">{usdLine}</p> : null}
      </CardContent>
    </Card>
  );
}

export default function LarissaDaoPage() {
  const stats = useStats();
  const usdPrice = stats.price ?? 0;
  const daoAddr = LARISSA_DAO_CONTRACT_CONFIG.address;
  const daoReady = !!daoAddr && daoAddr.length > 2;

  const monthKeys = useMemo(() => buildDaoMonthKeys(2024, 6, new Date()), []);

  const globalContracts = useMemo(() => {
    if (!daoReady) return [];
    return GLOBAL_FNS.map((functionName) => ({
      address: daoAddr,
      abi: LARISSA_DAO_CONTRACT_CONFIG.abi,
      chainId: larissaChain.id,
      functionName,
    }));
  }, [daoAddr, daoReady]);

  const monthlyContracts = useMemo(() => {
    if (!daoReady) return [];
    const out: Array<{
      address: typeof daoAddr;
      abi: typeof LARISSA_DAO_CONTRACT_CONFIG.abi;
      chainId: typeof larissaChain.id;
      functionName: (typeof MONTHLY_FNS)[number];
      args: readonly [string];
    }> = [];
    for (const key of monthKeys) {
      for (const functionName of MONTHLY_FNS) {
        out.push({
          address: daoAddr,
          abi: LARISSA_DAO_CONTRACT_CONFIG.abi,
          chainId: larissaChain.id,
          functionName,
          args: [key],
        });
      }
    }
    return out;
  }, [daoAddr, daoReady, monthKeys]);

  const { data: nativeBal } = useBalance({
    address: daoAddr,
    chainId: larissaChain.id,
    query: { enabled: daoReady },
  });

  const {
    data: globalData,
    isPending: globalPending,
    isError: globalError,
  } = useReadContracts({
    contracts: globalContracts,
    query: { enabled: daoReady && globalContracts.length > 0 },
  });

  const {
    data: monthlyData,
    isPending: monthlyPending,
    isError: monthlyError,
  } = useReadContracts({
    contracts: monthlyContracts,
    query: { enabled: daoReady && monthlyContracts.length > 0 },
  });

  const globalVals = useMemo(() => {
    if (!globalData) return null;
    const nums = globalData.map((x) => (x.status === 'success' ? (x.result as bigint) : 0n));
    return {
      projectFunding: nums[0],
      developmentFarmAsic: nums[1],
      exchangeListingReward: nums[2],
    };
  }, [globalData]);

  const calendarYear = new Date().getFullYear();

  const monthlyRows: MonthlyRow[] = useMemo(() => {
    if (!monthlyData) return [];
    const per = MONTHLY_FNS.length;
    const rows = monthKeys.map((key, mi) => {
      const slice = monthlyData.slice(mi * per, mi * per + per);
      const num = (i: number) => {
        const s = slice[i];
        if (!s || s.status !== 'success') {
          if (i === 1) return false;
          return 0n;
        }
        return s.result as bigint | boolean;
      };
      return {
        key,
        label: formatDaoMonthLabel(key),
        membersLrs: num(0) as bigint,
        finalized: num(1) as boolean,
        nftLastID: num(2) as bigint,
        perL1: num(3) as bigint,
        perL2: num(4) as bigint,
        perL3: num(5) as bigint,
      };
    });
    return rows.slice().reverse();
  }, [monthlyData, monthKeys]);

  const monthSummary = useMemo(() => {
    let finalized = 0;
    let withPool = 0;
    for (const r of monthlyRows) {
      if (r.finalized) finalized += 1;
      if (r.membersLrs > 0n) withPool += 1;
    }
    return { finalized, withPool, total: monthlyRows.length };
  }, [monthlyRows]);

  const monthlyByYear = useMemo(() => {
    const map = new Map<number, MonthlyRow[]>();
    for (const row of monthlyRows) {
      const [ys] = row.key.split('-');
      const y = Number(ys);
      if (!Number.isFinite(y)) continue;
      if (!map.has(y)) map.set(y, []);
      map.get(y)!.push(row);
    }
    return [...map.entries()].sort((a, b) => b[0] - a[0]);
  }, [monthlyRows]);

  const loading = !daoReady || globalPending || monthlyPending;
  const readError = globalError || monthlyError;

  const explorerBase = larissaChain.blockExplorers?.default?.url ?? '';

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 px-4">
          <SidebarTrigger className="-ml-1 cursor-pointer" />
          <Separator orientation="vertical" className="h-6 mx-2" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/blockchain/stats">Blockchain</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Larissa DAO</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="ml-auto">
            <ConnectButton />
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-6 p-4 pt-0 max-w-7xl w-full mx-auto pb-10">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">Larissa DAO treasury</h1>
              {daoReady && explorerBase ? (
                <a
                  href={`${explorerBase}/address/${daoAddr}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/90 transition-colors duration-200 cursor-pointer"
                >
                  Contract
                  <RiExternalLinkLine className="size-4" aria-hidden />
                </a>
              ) : null}
            </div>
            <p className="text-sm text-muted-foreground max-w-3xl">
              On-chain native <span className="text-foreground/90 font-medium">LRS</span> from Larissa DAO V4: treasury
              buckets and monthly NFT member pools with per-tier LRS after finalization. Month keys match the contract (
              <code className="text-xs bg-muted px-1 rounded">YYYY-M</code>
              ).
            </p>
          </div>

          {!daoReady && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100 flex gap-2">
              <RiErrorWarningLine className="size-5 shrink-0 mt-0.5" aria-hidden />
              <span>
                Set <code className="text-xs">VITE_LARISSA_DAO_CONTRACT_ADDRESS</code> to your deployed LarissaDaoV4
                address to load live data.
              </span>
            </div>
          )}

          {readError && daoReady && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200 flex gap-2">
              <RiErrorWarningLine className="size-5 shrink-0" aria-hidden />
              <span>Some contract reads failed. Check RPC connectivity or contract address.</span>
            </div>
          )}

          <Card className="border-violet-500/20 bg-gradient-to-br from-sidebar to-sidebar/60">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <RiPieChartLine className="size-5 text-violet-400" aria-hidden />
                How income is routed
              </CardTitle>
              <CardDescription>
                Native <span className="text-foreground/90 font-medium">LRS</span> enters via{' '}
                <code className="text-xs bg-background/50 px-1 rounded">daoRefuel</code>. Incoming LRS is split: 23%
                member pool (finalized monthly for NFT tiers), 20% project funding, 35% development / farm / ASIC, and 22%
                exchange listing — all tracked here as native balances on this contract.
              </CardDescription>
            </CardHeader>
          </Card>

          {loading && daoReady ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <RiLoader4Line className="size-5 animate-spin" aria-hidden />
              Loading contract state…
            </div>
          ) : null}

          <div>
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <RiStackLine className="size-5 text-primary" aria-hidden />
              Contract native balance
            </h2>
            <Card className="max-w-md border-border bg-sidebar/80">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground mb-1">LRS on DAO contract</p>
                <p className="text-xl font-semibold tabular-nums">
                  {nativeBal?.value != null
                    ? `${Number(formatEther(nativeBal.value)).toLocaleString(undefined, {
                        minimumFractionDigits: 4,
                        maximumFractionDigits: 4,
                      })} LRS`
                    : '—'}
                </p>
                {nativeBal?.value != null && usdPrice > 0 ? (
                  <p className="text-sm text-muted-foreground tabular-nums mt-1">
                    {(Number(formatEther(nativeBal.value)) * usdPrice).toLocaleString(undefined, {
                      style: 'currency',
                      currency: 'USD',
                      maximumFractionDigits: 2,
                    })}
                  </p>
                ) : null}
              </CardContent>
            </Card>
          </div>

          {globalVals && (
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <RiFundsLine className="size-5 text-amber-500" aria-hidden />
                Treasury buckets (unclaimed balance)
              </h2>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <StatTile
                  title="Project funding"
                  subtitle="20% allocation bucket"
                  icon={RiCoinsLine}
                  lrsLine={`${fmtLrs(globalVals.projectFunding, usdPrice).lrs} LRS`}
                  usdLine={fmtLrs(globalVals.projectFunding, usdPrice).usd}
                />
                <StatTile
                  title="Development / Farm / ASIC"
                  subtitle="35% allocation"
                  icon={RiPlantLine}
                  lrsLine={`${fmtLrs(globalVals.developmentFarmAsic, usdPrice).lrs} LRS`}
                  usdLine={fmtLrs(globalVals.developmentFarmAsic, usdPrice).usd}
                />
                <StatTile
                  title="Exchange listing"
                  subtitle="22% native LRS bucket"
                  icon={RiFundsLine}
                  lrsLine={`${fmtLrs(globalVals.exchangeListingReward, usdPrice).lrs} LRS`}
                  usdLine={fmtLrs(globalVals.exchangeListingReward, usdPrice).usd}
                />
              </div>
            </div>
          )}

          <div>
            <h2 className="text-lg font-semibold mb-1 flex items-center gap-2" id="nft-member-rewards">
              <RiCalendarLine className="size-5 text-primary" aria-hidden />
              NFT member rewards
            </h2>
            <p className="text-sm text-muted-foreground mb-3">
              June 2024 through {formatDaoMonthLabel(monthKeys[monthKeys.length - 1] ?? '')}. Open a year, then a month,
              to see the native LRS member pool and per-tier amounts after{' '}
              <code className="text-xs bg-muted px-1 rounded">finalizeRewards</code>.
            </p>
            {monthlyRows.length > 0 ? (
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="outline" className="border-border cursor-default font-normal">
                  {monthSummary.total} months in range
                </Badge>
                <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 cursor-default font-normal">
                  {monthSummary.finalized} finalized
                </Badge>
                <Badge variant="outline" className="border-violet-500/35 text-violet-200 cursor-default font-normal">
                  {monthSummary.withPool} with member pool
                </Badge>
              </div>
            ) : null}

            <div className="flex flex-col gap-3 motion-reduce:transition-none">
              {monthlyByYear.map(([year, rows]) => {
                const yearFinalized = rows.filter((r) => r.finalized).length;
                const yearWithPool = rows.filter((r) => r.membersLrs > 0n).length;
                return (
                  <Collapsible key={year} defaultOpen={year === calendarYear} className="group/year">
                    <Card className="border-border bg-sidebar/80 overflow-hidden transition-colors duration-200 motion-safe:group-data-[state=open]/year:border-primary/25 motion-safe:group-data-[state=open]/year:shadow-sm motion-safe:group-data-[state=open]/year:shadow-primary/5">
                      <CollapsibleTrigger
                        type="button"
                        className={cn(
                          'flex w-full items-center gap-3 px-4 py-3.5 text-left cursor-pointer',
                          'hover:bg-muted/30 transition-colors duration-200',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                        )}
                      >
                        <RiArrowDownSLine
                          className="size-5 shrink-0 text-muted-foreground transition-transform duration-200 ease-out motion-reduce:transition-none group-data-[state=open]/year:rotate-180"
                          aria-hidden
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-base font-semibold text-foreground tabular-nums">{year}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {rows.length} months · {yearFinalized} finalized · {yearWithPool} with pool
                          </p>
                        </div>
                        <Badge variant="outline" className="border-border cursor-default font-normal shrink-0 hidden sm:inline-flex">
                          {year === calendarYear ? 'Current year' : 'Past'}
                        </Badge>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="motion-reduce:animate-none border-t border-border/60 bg-background/25">
                        <div className="p-3 sm:p-4 flex flex-col gap-2">
                          {rows.map((row) => {
                            const hasActivity = row.membersLrs > 0n || row.finalized;
                            return (
                              <Collapsible key={row.key} defaultOpen={false} className="group/month">
                                <div
                                  className={cn(
                                    'rounded-lg border transition-colors duration-200',
                                    hasActivity ? 'border-primary/20 bg-sidebar/60' : 'border-border/80 bg-sidebar/40',
                                  )}
                                >
                                  <CollapsibleTrigger
                                    type="button"
                                    className={cn(
                                      'flex w-full items-start gap-3 px-3 py-3 sm:px-4 text-left cursor-pointer',
                                      'hover:bg-muted/20 transition-colors duration-200 rounded-lg',
                                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                                    )}
                                  >
                                    <RiArrowDownSLine
                                      className="size-4 shrink-0 mt-0.5 text-muted-foreground transition-transform duration-200 ease-out motion-reduce:transition-none group-data-[state=open]/month:rotate-180"
                                      aria-hidden
                                    />
                                    <div className="min-w-0 flex-1 space-y-1">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-sm font-medium text-foreground">{row.label}</span>
                                        <Badge
                                          variant="outline"
                                          className={cn(
                                            'cursor-default font-normal text-xs',
                                            row.finalized
                                              ? 'border-emerald-500/50 text-emerald-300'
                                              : 'border-amber-500/40 text-amber-200',
                                          )}
                                        >
                                          {row.finalized ? 'Finalized' : 'Open'}
                                        </Badge>
                                      </div>
                                      <p className="font-mono text-[11px] sm:text-xs text-muted-foreground">Key {row.key}</p>
                                      <p className="text-sm tabular-nums text-foreground/90">
                                        Pool{' '}
                                        <span className="font-semibold">{fmtLrs(row.membersLrs, usdPrice).lrs}</span> LRS
                                        {fmtLrs(row.membersLrs, usdPrice).usd ? (
                                          <span className="text-muted-foreground font-normal">
                                            {' '}
                                            ({fmtLrs(row.membersLrs, usdPrice).usd})
                                          </span>
                                        ) : null}
                                      </p>
                                    </div>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent className="motion-reduce:animate-none px-3 pb-3 sm:px-4 sm:pb-4 pt-0">
                                    <div className="rounded-md border border-border/60 bg-background/40 p-3 space-y-3">
                                      <div>
                                        <p className="text-xs text-muted-foreground uppercase tracking-wide">
                                          Member pool (LRS)
                                        </p>
                                        <p className="text-lg font-semibold tabular-nums mt-1">
                                          {fmtLrs(row.membersLrs, usdPrice).lrs} LRS
                                        </p>
                                        {fmtLrs(row.membersLrs, usdPrice).usd ? (
                                          <p className="text-xs text-muted-foreground tabular-nums">
                                            {fmtLrs(row.membersLrs, usdPrice).usd}
                                          </p>
                                        ) : null}
                                      </div>
                                      <div>
                                        <p className="text-xs text-muted-foreground mb-2">
                                          After finalization — max eligible NFT id{' '}
                                          <span className="text-foreground font-mono tabular-nums">{row.nftLastID.toString()}</span>
                                        </p>
                                        <div className="overflow-x-auto rounded-md border border-border/50">
                                          <table className="w-full text-sm">
                                            <thead>
                                              <tr className="border-b border-border bg-muted/20 text-left text-muted-foreground">
                                                <th className="py-2.5 px-3 font-medium">Tier</th>
                                                <th className="py-2.5 px-3 font-medium">LRS / NFT</th>
                                              </tr>
                                            </thead>
                                            <tbody className="tabular-nums">
                                              {[
                                                { t: 'L1', l: row.perL1 },
                                                { t: 'L2', l: row.perL2 },
                                                { t: 'L3', l: row.perL3 },
                                              ].map((x) => (
                                                <tr key={x.t} className="border-b border-border/40 last:border-0">
                                                  <td className="py-2 px-3 font-medium">{x.t}</td>
                                                  <td className="py-2 px-3">{fmtLrs(x.l, usdPrice).lrs}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    </div>
                                  </CollapsibleContent>
                                </div>
                              </Collapsible>
                            );
                          })}
                        </div>
                      </CollapsibleContent>
                    </Card>
                  </Collapsible>
                );
              })}
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
